import React, { useState, useCallback, useEffect } from "react";

interface ErrorInfo {
  id: string;
  message: string;
  timestamp: Date;
  context?: string;
  retryable: boolean;
  retryCount: number;
  maxRetries: number;
  onRetry?: () => void;
}

interface GlobalErrorState {
  errors: ErrorInfo[];
  addError: (error: Omit<ErrorInfo, "id" | "timestamp" | "retryCount">) => void;
  removeError: (id: string) => void;
  retryError: (id: string) => void;
  clearAllErrors: () => void;
}

export const useGlobalErrorHandler = (): GlobalErrorState => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  const addError = useCallback(
    (errorInfo: Omit<ErrorInfo, "id" | "timestamp" | "retryCount">) => {
      const id = `error_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const newError: ErrorInfo = {
        ...errorInfo,
        id,
        timestamp: new Date(),
        retryCount: 0,
      };

      setErrors((prev) => [...prev, newError]);

      // Auto-remove non-retryable errors after 10 seconds
      if (!errorInfo.retryable) {
        setTimeout(() => {
          setErrors((prev) => prev.filter((e) => e.id !== id));
        }, 10000);
      }
    },
    []
  );

  const removeError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const retryError = useCallback((id: string) => {
    setErrors((prev) =>
      prev.map((error) => {
        if (error.id === id && error.retryCount < error.maxRetries) {
          const newRetryCount = error.retryCount + 1;

          // Call the retry function if provided
          if (error.onRetry) {
            try {
              error.onRetry();
            } catch (err) {
              console.error("Error during retry:", err);
            }
          }

          return {
            ...error,
            retryCount: newRetryCount,
          };
        }
        return error;
      })
    );
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Auto-retry retryable errors with exponential backoff
  useEffect(() => {
    const retryableErrors = errors.filter(
      (e) => e.retryable && e.retryCount < e.maxRetries
    );

    if (retryableErrors.length > 0) {
      const timeout = setTimeout(() => {
        retryableErrors.forEach((error) => {
          retryError(error.id);
        });
      }, Math.pow(2, retryableErrors[0].retryCount) * 1000); // Exponential backoff

      return () => clearTimeout(timeout);
    }
  }, [errors, retryError]);

  return {
    errors,
    addError,
    removeError,
    retryError,
    clearAllErrors,
  };
};

// Global error context for app-wide error handling
export const GlobalErrorContext = React.createContext<GlobalErrorState | null>(
  null
);

// Hook to use global error handler
export const useGlobalError = () => {
  const context = React.useContext(GlobalErrorContext);
  if (!context) {
    throw new Error("useGlobalError must be used within a GlobalErrorProvider");
  }
  return context;
};
